import MaterialTable, { Column, Options } from '@material-table/core';
import { Button, Container, Typography } from '@mui/material';
import isNull from 'lodash/isNull';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { sortByFullName, TableOptions } from '../../shared/tableUtils';
import { RootState } from '../../store';
import {
  createTechnicalSupportUsers,
  deleteUser,
  getCustomerList,
  setCreateUserSuccessful,
  setDeleteUserSuccessful,
} from '../../store/customers/actionCreators';
import { Owner } from '../../store/customers/customerTypes';
import { getPharmacies } from '../../store/pharmacy/actionCreators';
import { HeaderWrapper } from '../Shared/HeaderWrapper';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { Wrapper } from '../Shared/Wrapper';
import DeleteIcon from '@mui/icons-material/Delete';
import { PRIVILEGE } from '../../shared/constants';
import NewModal from '../Shared/NewModal';
import CreateNewPharmacyTechForm, {
  NewPharmacyTechFormValues,
} from './CreateNewPharmacyTechForm';
import { FormikProps } from 'formik';
import DeleteUserModal from '../Caregivers/components/DeleteUserModal';

const PharmacyTechUsers = () => {
  const {
    isLoading,
    dashboardUserInfo,
    facilityCustomers,
    createUserSuccessful,
    deleteUserSuccessful,
    pharmacies,
  } = useSelector((state: RootState) => {
    const { dashboardUser, customers, pharmacy } = state;
    return {
      isLoading: customers.isLoading,
      dashboardUserInfo: dashboardUser.dashboardUserInfo,
      facilityCustomers: customers.facilityCustomers,
      createUserSuccessful: customers.createUserSuccessful,
      deleteUserSuccessful: customers.deleteUserSuccessful,
      pharmacies: pharmacy.pharmacies,
    };
  });

  const dispatch = useDispatch();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null as number | null,
  });
  const formikRef = useRef<FormikProps<NewPharmacyTechFormValues>>(null);

  // Pharmacy names for the Add-new dropdown, sourced from GET /pharmacy/.
  const pharmacyOptions = useMemo(
    () =>
      (pharmacies ?? []).map((pharmacy) => ({
        value: String(pharmacy.id),
        label: pharmacy.name,
      })),
    [pharmacies],
  );

  const onSubmit = (values: NewPharmacyTechFormValues) => {
    if (formikRef.current) {
      // Same create endpoint as customer care, but with the pharmacy-tech
      // privilege and the selected pharmacy's id.
      const submissionData = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        privilege_id: PRIVILEGE.PHARMACY_TECH,
        pharmacy_id: Number(values.pharmacy_id),
      };
      dispatch(createTechnicalSupportUsers(submissionData, 'pharmacy tech'));
    }
  };

  const handleDelete = (customerId: number) => {
    setDeleteModal({ isOpen: true, userId: customerId });
  };

  const handleCloseModal = () => {
    setDeleteModal({ isOpen: false, userId: null });
  };

  const handleConfirmDelete = () => {
    if (deleteModal.userId) {
      dispatch(deleteUser(deleteModal.userId));
    }
  };

  useEffect(() => {
    dispatch(getPharmacies());
    dispatch(getCustomerList(null, PRIVILEGE.PHARMACY_TECH));
  }, [dispatch]);

  useEffect(() => {
    if (createUserSuccessful) {
      setIsAddModalOpen(false);
      dispatch(setCreateUserSuccessful(false));
      dispatch(getCustomerList(null, PRIVILEGE.PHARMACY_TECH));
    }
    if (deleteUserSuccessful) {
      handleCloseModal();
      dispatch(setDeleteUserSuccessful(false));
      dispatch(getCustomerList(null, PRIVILEGE.PHARMACY_TECH));
    }
  }, [dispatch, createUserSuccessful, deleteUserSuccessful]);

  if (isNull(facilityCustomers) || isLoading) {
    return <LoadingSpinner />;
  }

  const materialTableColumns: Column<Owner>[] = [
    {
      title: 'Name',
      field: 'name',
      customSort: sortByFullName,
    },
    {
      title: 'Username',
      field: 'email',
    },
    {
      title: 'Date of birth',
      field: 'date_of_birth',
      type: 'date',
    },
    {
      title: 'Actions',
      field: 'actions',
      sorting: false,
      render: (rowData) =>
        rowData.customer_id !== dashboardUserInfo?.customer_id && (
          <DeleteIcon
            style={{ cursor: 'pointer' }}
            onClick={() => handleDelete(rowData.customer_id)}
          />
        ),
    },
  ];

  return (
    <Container maxWidth="lg">
      <Helmet>
        <title>Ōmcare - Pharmacy Tech</title>
      </Helmet>
      <Wrapper>
        <HeaderWrapper>
          <Typography variant={'h1'}>Pharmacy tech list</Typography>
          <div>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setIsAddModalOpen(true)}
            >
              Add new
            </Button>
          </div>
        </HeaderWrapper>

        <MaterialTable
          columns={materialTableColumns}
          data={facilityCustomers ? facilityCustomers.sort(sortByFullName) : []}
          options={{
            idSynonym: 'customer_id',
            pageSize: 150,
            pageSizeOptions: [150, 250, 350],
            ...(TableOptions as Partial<Options<Owner>>),
          }}
        />
      </Wrapper>
      {isAddModalOpen && (
        <NewModal
          showCloseX
          title="Add new pharmacy tech"
          onClose={() => {
            setIsAddModalOpen(false);
          }}
          actions={{
            primaryAction: {
              onClick: () => {
                if (formikRef.current) {
                  formikRef.current.submitForm();
                }
              },
            },
          }}
          backdropBlur
          isLoading={isLoading}
        >
          <CreateNewPharmacyTechForm
            formRef={formikRef}
            onSubmit={onSubmit}
            pharmacyOptions={pharmacyOptions}
          />
        </NewModal>
      )}
      {deleteModal.isOpen && (
        <DeleteUserModal
          onClose={handleCloseModal}
          onDelete={handleConfirmDelete}
        />
      )}
    </Container>
  );
};

export default PharmacyTechUsers;
